import {
  useMutation,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";

import { noteService } from "../../services/noteService";
import { ensureOk } from "../../utils/ensureOk";

/**
 * Invalidates every notes/tasks cache so changes from one entry-point
 * (patient page, My tasks sidebar, single-note view) propagate everywhere
 * the same record is shown.
 *
 * `refetchType: "all"` is required because the global QueryClient is configured
 * with `refetchOnMount: false` (see config/query.ts). Without it, invalidated
 * but inactive queries (e.g. the patient page note list while the user is on
 * My Tasks) would stay stale even after navigating back — the user would see
 * the pre-mutation state until a hard refresh.
 *
 * Exported so create/edit/delete callers that don't go through a TanStack
 * mutation (they call noteService.* directly) can still trigger the full
 * cross-view refresh from their modal `onClose` handlers.
 */
export function invalidateAllNoteCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: ["patient-notes-infinite"],
    refetchType: "all",
  });
  queryClient.invalidateQueries({
    queryKey: ["my-assigned-tasks-infinite"],
    refetchType: "all",
  });
  queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === "string" &&
      (query.queryKey[0].startsWith("patient-notes-table-") ||
        query.queryKey[0].startsWith("note-")),
    refetchType: "all",
  });
}

export function useToggleNoteUrgent() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      noteId,
      isUrgent,
    }: {
      noteId: number;
      isUrgent: boolean;
    }) => {
      return ensureOk(await noteService.setUrgent(noteId, isUrgent));
    },
    onSuccess: () => invalidateAllNoteCaches(queryClient),
  });

  return { mutateAsync, isPending };
}

export function useToggleNoteComplete() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      noteId,
      isCompleted,
    }: {
      noteId: number;
      isCompleted: boolean;
    }) => {
      return ensureOk(await noteService.setCompleted(noteId, isCompleted));
    },
    onSuccess: () => invalidateAllNoteCaches(queryClient),
  });

  return { mutateAsync, isPending };
}

export function useGenerateAiSummary() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (noteId: number) => {
      return noteService.generateAiSummary(noteId);
    },
    onSuccess: (data, noteId) => {
      // Update the cache directly with the new AI summary instead of refetching
      // This prevents the modal from reloading
      queryClient.setQueryData([`note-${noteId}`, noteId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          aiSummary: data.aiSummary,
        };
      });
    },
  });

  return { generateAiSummary: mutateAsync, isGenerating: isPending };
}
